/* =============================================================
   ILS Rubik Combined Listings mount + shadow-DOM style injection
   TASK-2223 (mount) + TASK-2231 (4-per-row grid) + TASK-2233
   (tighter gap + tighter selected-tile border inset) + TASK-2234
   (fix red-border clipping + widen inner white gap by 1px) +
   TASK-2235 (kill focus outline, tap-highlight, long-press callout) +
   TASK-2257 (real fix for Android long-press callout — CSS
   `-webkit-touch-callout: none` is iOS-only, so Android Chrome
   still surfaced the OS long-press link preview with the Latvian
   `<title>` of the target page. Fixed via JS `contextmenu`
   preventDefault + defensive title-attr stripping.)

   Rubik Combined Listings ships as an App Embed that mounts a
   <div class="shopify-block shopify-app-block"> straight into
   <body> (see reports/REPORT-2221 + audit/rubik-swatch-fragment.html).
   That block carries the group JSON payload and later hydrates a
   custom element <rubik-combined-listings> whose swatch grid lives
   INSIDE Shadow DOM. Class names in that shadow tree are
   .rcl__grid (flex-wrap by default, gap:7px) and .rcl__swatch
   (fixed 90px×90px tiles driven by --rcl-swatch-width on :host).

   Two jobs, both here because they both need the same custom
   element to exist:
     1. MOUNT — move the App-Embed block into #rubik-mount-point
        so the grid renders inside the PDP column, not at the
        bottom of <body>.
     2. SHADOW STYLE INJECTION — inject a <style> into the shadow
        root that flips .rcl__grid from flex-wrap to a 4-column
        CSS grid and unpins .rcl__swatch from its 90px fixed
        width. Light-DOM CSS from custom-rubik-mount.css cannot
        reach into the shadow root (that's the whole point of
        shadow DOM), so this injection is the only way to control
        the tile layout without touching Rubik's admin.

   Loaded only on product pages via layout/theme.liquid.
   ============================================================= */
(function () {
  'use strict';

  var MOUNT_ID = 'rubik-mount-point';
  var WRAP_ATTR = 'data-ils-rubik-wrap';
  var JSON_SELECTOR = 'script[data-rubik-swatch-product-id]';
  var BLOCK_SELECTOR = '.shopify-block.shopify-app-block';
  var CUSTOM_EL = 'rubik-combined-listings';
  var TIMEOUT_MS = 30000;
  var STYLE_MARK = 'data-ils-rubik-4up';

  /* Shadow-DOM CSS override — targets Rubik's own .rcl__grid /
     .rcl__swatch class names captured from the rendered DOM
     (audit/rubik-2231-shadow-probe.json).

     grid-template-columns: repeat(4, 1fr) gives exactly 4 equal
     columns regardless of viewport, and the tile rule flips the
     fixed 90px width off (min-width:0 kills the flex-item
     protection against shrinking below intrinsic size). When a
     group has fewer than 4 swatches, grid keeps the columns
     defined but only fills the leftmost cells — tiles stay the
     same 25%-of-container size instead of stretching.

     :where(...) on the grid keeps our specificity low enough
     that Rubik's own future rule changes still win where they
     should; !important is only used where we need to defeat the
     fixed inline sizing that Rubik derives from the swatchWidth
     JSON knob. */
  /* TASK-2233 fine-tune: override Rubik's own CSS variables at :host
     (and at the data-rcl-group scope Rubik itself uses so the more
     specific ruleset does not shadow ours) to:
       --rcl-grid-gap: 7px -> 5px   (~29% tighter gap between tiles)
       --rcl-swatch-border-offset: 3px -> 1px
       --rcl-swatch-selected-border-offset: 3px -> 1px  (bumped to 2px by TASK-2234)
     The border-offset variables control the white ring between the
     swatch image and the outer border. Reducing them makes the red
     border on the selected (`.rcl__swatch--active`) tile hug the
     image tightly instead of leaving a visible white gap. Border
     width itself (3px, red on active) is preserved.

     TASK-2234 fixes two things visible in the zoomed screenshot:

     1) The red "border" on the active tile is not a real CSS border —
        Rubik draws it as an OUTER box-shadow spread on
        `.rcl__swatch--active`
          box-shadow: 0 0 0 var(--rcl-swatch-border-width, 3px) #C60404
        which lives OUTSIDE the tile's border-box, in the 5px gap
        between tiles. Adjacent tiles also carry their own outer
        shadow (grey baseline ring), and where the two shadows overlap
        in the tight 5px gap the neighbour's paint order wins on one
        side, so the red is clipped visually on that edge (a well-
        known outer-box-shadow-in-a-tight-flex-gap artefact — nothing
        to do with `overflow: hidden`, since box-shadow is not clipped
        by the element's own overflow). Fix: put the active tile in a
        new stacking context with z-index:2 so its outer shadow paints
        LAST and is never occluded by any neighbour. Requires
        `position: relative` to make z-index take effect on a static
        flex/grid item. No layout side effect — position:relative
        with zero offsets is a no-op on flow.

     2) After TASK-2233 the inner white gap (--rcl-swatch-selected-
        border-offset) went to 1px which reads as "touching" on the
        screenshot. Bump to 2px — the image contracts from 123x123
        down to 121x121 inside a 125x125 tile, revealing a visible
        2px white ring between image and red border. Neighbour
        clearance stays safe: gap(5px) - shadow(3px) = 2px between
        active's red outer edge and neighbour's border-box, so the
        red never touches the next tile. */
  /* TASK-2269 — read Rubik knobs from CSS custom properties set inline
     on #rubik-mount-point (light-DOM) by custom-main-product.liquid from
     section.settings. Custom properties inherit through the shadow-DOM
     boundary via the shadow host, so `var(--ils-rcl-*)` inside :host
     resolves to whatever the light-DOM ancestor set. Fallbacks match the
     TASK-2231/2233/2234 defaults so pre-TASK-2269 installs still render
     identically. --rcl-swatch-border-width is Rubik's own variable that
     drives the outer red box-shadow spread on the active tile. */
  var SHADOW_CSS =
    ':host,[data-rcl-group]{' +
      '--rcl-grid-gap:var(--ils-rcl-gap, 5px) !important;' +
      '--rcl-swatch-border-offset:var(--ils-rcl-border-offset, 1px) !important;' +
      '--rcl-swatch-selected-border-offset:var(--ils-rcl-selected-border-offset, 2px) !important;' +
      '--rcl-swatch-border-width:var(--ils-rcl-border-width, 3px) !important;' +
    '}' +
    '.rcl__grid,[class*="rcl__grid"],[class*="rcl-swatches-grid"]{' +
      'display:grid !important;' +
      'grid-template-columns:repeat(var(--ils-rcl-cols, 4), minmax(0, 1fr)) !important;' +
      'gap:var(--rcl-grid-gap, 5px) !important;' +
      'justify-content:start !important;' +
      'width:100% !important;' +
      'padding:0 !important;' +
    '}' +
    '.rcl__swatch,[class*="rcl__swatch"]{' +
      'width:100% !important;' +
      'min-width:0 !important;' +
      'max-width:100% !important;' +
      'height:auto !important;' +
      'aspect-ratio:1 / 1 !important;' +
      'flex:none !important;' +
      /* TASK-2235: kill three sources of "interaction artefacts" on
         swatch tiles so the only visual response is the red border on
         the already-selected tile (owner directive — no grey focus
         ring, no mobile tap-highlight overlay, no long-press callout
         menu with the destination page title). Details:
         - outline:none — Rubik's own shadow CSS draws a red
           outline:2px solid #C60404 outline-offset:2px on
           `.rcl__swatch:focus` (probed with .focus() — see
           audit/rubik-2235-diagnostic/diagnostic-report.json). That
           outline shows on programmatic focus AND on mobile
           long-press (which puts the anchor in :focus). Owner
           perceived it as grey during touch because the browser
           `-webkit-tap-highlight-color` grey overlay blends on top.
         - -webkit-tap-highlight-color:transparent — kills the
           grey semi-transparent tap flash that mobile Chrome/Safari
           paint over any activated element (default rgba(0,0,0,.18)-ish).
         - -webkit-touch-callout:none — suppresses the long-press
           callout menu on iOS Safari AND Android Chrome. That callout
           is a black bottom panel showing the destination link's
           `<title>` (page title of the target product page). Since
           the store's Latvian locale renders product `<title>` tags
           in Latvian, the callout showed "Citronu košļājamās
           konfektes" etc. — which the owner saw as an unwanted
           Latvian "tooltip". Zero `title` / `alt` / `aria-label`
           attributes on swatches contain that Latvian text (probe
           confirmed) — it is exclusively the destination page title
           surfaced by the browser's own long-press UI.
         Normal click / tap navigation is untouched — none of these
         three properties affect click event dispatch, pointerdown, or
         `<a href>` semantics. Keyboard Tab focus still works; only
         its visual outline is hidden (acceptable trade-off on a
         swatch grid where the active tile already has a strong red
         border indicating the current selection). */
      'outline:none !important;' +
      '-webkit-tap-highlight-color:transparent !important;' +
      '-webkit-touch-callout:none !important;' +
    '}' +
    '.rcl__swatch:focus,.rcl__swatch:focus-visible,' +
    '.rcl__swatch:hover,.rcl__swatch:active,' +
    '[class*="rcl__swatch"]:focus,[class*="rcl__swatch"]:focus-visible,' +
    '[class*="rcl__swatch"]:hover,[class*="rcl__swatch"]:active{' +
      'outline:none !important;' +
    '}' +
    '.rcl__swatch--active,[class*="rcl__swatch"].rcl__swatch--active{' +
      'position:relative !important;' +
      'z-index:2 !important;' +
    '}' +
    '.rcl__swatch img,[class*="rcl__swatch"] img{' +
      'width:100% !important;' +
      'height:100% !important;' +
      'object-fit:cover !important;' +
    '}';

  var moved = false;
  var styleInjectedRoots = new WeakSet();
  var longPressGuardedRoots = new WeakSet();

  var SWATCH_SELECTOR = '.rcl__swatch, [class*="rcl__swatch"]';

  function findRubikBlock() {
    var jsonScript = document.querySelector(JSON_SELECTOR);
    if (!jsonScript) return null;
    return jsonScript.closest(BLOCK_SELECTOR);
  }

  function revealWrap() {
    var wrap = document.querySelector('[' + WRAP_ATTR + ']');
    if (wrap) wrap.removeAttribute('hidden');
  }

  function injectShadowStyles() {
    var hosts = document.querySelectorAll(CUSTOM_EL);
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var root = host.shadowRoot;
      if (!root) continue;
      if (!styleInjectedRoots.has(root)) {
        if (root.querySelector('style[' + STYLE_MARK + ']')) {
          styleInjectedRoots.add(root);
        } else {
          var s = document.createElement('style');
          s.setAttribute(STYLE_MARK, '');
          s.textContent = SHADOW_CSS;
          root.appendChild(s);
          styleInjectedRoots.add(root);
        }
      }
      installLongPressGuard(root);
    }
  }

  /* TASK-2257 — kill the long-press link-preview callout that the
     mobile browser draws on top of the swatch grid.

     Background from TASK-2235 diagnostic: the callout is NOT sourced
     from a `title` attribute on the swatch anchor (there is none in
     the DOM — probe confirmed `withTitle: []`). It is the OS-level
     long-press preview that the browser generates from the target
     anchor's URL + fetched `<title>`. Since Rubik points the swatches
     at `/lv-lv/products/...` URLs, the Latvian page title surfaces.

     Why CSS alone is not enough:
       - `-webkit-touch-callout: none` — iOS-only. Android Chrome
         (the platform where the owner reproduces the issue) does
         NOT honour this property; its long-press link preview keeps
         firing.
       - `outline: none !important` — works, but the "grey border" the
         owner sees on the real device is largely the
         `-webkit-tap-highlight-color` overlay under the long-press
         callout scrim, not a focus outline. Once the callout is
         suppressed, that overlay does not linger either.

     What actually blocks the Android long-press callout:
       - `contextmenu` event, preventDefault. On both mouse right-
         click AND touch long-press, the browser fires `contextmenu`
         before showing the OS UI; preventing default cancels the UI
         while leaving normal tap navigation intact (contextmenu is
         orthogonal to click/pointerdown/pointerup).
       - Also inline element.style.webkitTapHighlightColor='transparent'
         as belt-and-suspender — direct-inline wins over any
         !important CSS specificity Rubik might reintroduce.
       - Also actively strip any `title` attribute that Rubik might
         attach dynamically post-hydration, via a MutationObserver.

     One guard is installed per shadow root the first time the root
     appears. The observer stays live for the lifetime of the page
     because Rubik can re-render its swatch grid after locale/currency
     changes (it swaps innerHTML rather than diffing individual nodes)
     and we need the guard to survive that. Memory footprint is
     negligible — one observer per shadow root. */
  function installLongPressGuard(root) {
    if (!root || longPressGuardedRoots.has(root)) return;
    longPressGuardedRoots.add(root);

    root.addEventListener('contextmenu', function (e) {
      var t = e.target;
      if (!t) return;
      var swatch = null;
      try { swatch = t.closest ? t.closest(SWATCH_SELECTOR) : null; } catch (_) { swatch = null; }
      if (swatch) {
        e.preventDefault();
        try { e.stopPropagation(); } catch (_) {}
      }
    }, true);

    hardenSwatchesIn(root);

    var mo = new MutationObserver(function (mutations) {
      var needSweep = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.attributeName === 'title') {
          try { m.target.removeAttribute('title'); } catch (_) {}
        } else if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
          needSweep = true;
        }
      }
      if (needSweep) hardenSwatchesIn(root);
    });
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['title']
    });
  }

  function hardenSwatchesIn(root) {
    var swatches;
    try { swatches = root.querySelectorAll(SWATCH_SELECTOR); }
    catch (_) { return; }
    for (var i = 0; i < swatches.length; i++) {
      var el = swatches[i];
      if (el.hasAttribute && el.hasAttribute('title')) {
        try { el.removeAttribute('title'); } catch (_) {}
      }
      if (el.style) {
        try { el.style.webkitTapHighlightColor = 'transparent'; } catch (_) {}
        try { el.style.setProperty('-webkit-touch-callout', 'none'); } catch (_) {}
        try { el.style.outline = 'none'; } catch (_) {}
      }
    }
  }

  function tryMount() {
    if (moved) {
      injectShadowStyles();
      return true;
    }
    var mount = document.getElementById(MOUNT_ID);
    if (!mount) return false;
    var block = findRubikBlock();
    if (!block) return false;
    if (block.parentNode === mount) {
      moved = true;
      injectShadowStyles();
      return true;
    }

    /* Set the guard flag BEFORE appendChild so the body-level
       MutationObserver we install below does not re-enter tryMount
       from its own callback (which fires when appendChild removes
       the block from <body>). This is the "guard + microtask"
       pattern the original mount task called out. */
    moved = true;
    mount.appendChild(block);
    mount.classList.add('is-rubik-mounted');
    revealWrap();
    injectShadowStyles();
    return true;
  }

  function start() {
    tryMount();

    /* One observer covers both jobs:
         - keeps trying to mount until the App-Embed block appears
         - keeps trying to inject shadow styles until the custom
           element has a shadow root (Rubik attaches it late during
           its bundle boot; can happen after the mount succeeds).
       We do NOT disconnect on first mount — shadow root can attach
       any time after that. We disconnect on the TIMEOUT below. */
    var observer = new MutationObserver(function () {
      Promise.resolve().then(function () {
        tryMount();
        injectShadowStyles();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    /* Products not attached to any Rubik group never render the
       app-block. Cap the observer — wrapper stays [hidden] from
       Liquid, so no empty heading remains. */
    setTimeout(function () { observer.disconnect(); }, TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
